import { useState, useEffect, useContext, useReducer, useMemo } from "react"
import { Transition } from "semantic-ui-react"
import { Moment } from "moment"

import { Elements } from "@stripe/react-stripe-js"
import { loadStripe, Stripe } from "@stripe/stripe-js"

import PriceDetails from "./PriceDetails"

import { CampingStyleProps, CampProps, SiteProps } from "../../interfaces"
import { discountType } from "~/constants"
import { validElectric, validRigType } from "~/types"
import { IPriceDetails } from "~/api2/price_details"
import { BookerContext } from "~/contexts/BookerContext"
import Spinner from "../ui/Spinner"
import { useDebouncedCallback } from "use-debounce"
import { PRICE_DURATIONS } from "~/constants/price_durations"
import CampDiscountProps from "~/interfaces/CampDiscountProps"
import CampingStyleDiscountProps from "~/interfaces/CampingStyleDiscountProps"
import { stripeOptionsFn } from "~/constants/stripe"
import api2 from '../../api2'
import IOrder from '~/interfaces/IOrder'
import { userParamsInitialState, userParamsReducer } from "~/reducers/userParamsReducer"

const stripePublicKey = import.meta.env.VITE_STRIPE_PUBKEY

interface ComponentProps {
  camp: CampProps
  camping_style: CampingStyleProps
  site: SiteProps
  startDate: Moment
  endDate: Moment
  visible: boolean
  setIsPaying: any
  userRigType: validRigType
  userRigLength: number
  userElectric: validElectric
  userPassengers: number
  userKidCampers: number
  onError: (errorMessage: string) => void
}

const discountRatesFor = (
  camp: CampProps,
  campingStyle: CampingStyleProps,
  duration: number,
): Record<string, CampDiscountProps | CampingStyleDiscountProps> => {
  let is_weekly = false
  let is_monthly = false

  if (duration >= 30) {
    is_monthly = true
  } else if (duration >= 7) {
    is_weekly = true
  }

  const campingStyleDiscountRates =
    campingStyle.discounts?.reduce(
      (obj, discount) => {
        const campingStyleDiscount = campingStyle.camping_style_discounts
          .filter((discount) => discount.is_bookable)
          .find((x) => x.discount_id == discount.id)

        if (
          campingStyleDiscount?.restricted_durations.includes(PRICE_DURATIONS.WEEKLY) &&
          (is_weekly || (is_monthly && !campingStyleDiscount?.restricted_durations.includes(PRICE_DURATIONS.MONTHLY)))
        ) {
          return obj
        } else if (campingStyleDiscount?.restricted_durations.includes(PRICE_DURATIONS.MONTHLY) && is_monthly) {
          return obj
        }

        if (campingStyleDiscount) obj[discount.slug] = { ...campingStyleDiscount, name: discount.name }
        return obj
      },
      {} as Record<string, CampingStyleDiscountProps & { name: string }>,
    ) || {}

  const campDiscountRates =
    camp.discounts?.reduce(
      (obj, discount) => {
        const campDiscount = camp.camp_discounts
          .filter((discount) => discount.is_bookable)
          .find((x) => x.discount_id == discount.id)
        if (
          campDiscount?.restricted_durations.includes(PRICE_DURATIONS.WEEKLY) &&
          (is_weekly || (is_monthly && !campDiscount?.restricted_durations.includes(PRICE_DURATIONS.MONTHLY)))
        ) {
          return obj
        } else if (campDiscount?.restricted_durations.includes(PRICE_DURATIONS.MONTHLY) && is_monthly) {
          return obj
        }
        if (campDiscount) {
          obj[discount.slug] = { ...campDiscount, name: discount.name }
        }
        return obj
      },
      {} as Record<string, CampDiscountProps & { name: string }>,
    ) || {}

  return { ...campDiscountRates, ...campingStyleDiscountRates }
}

const Payment = (props: ComponentProps) => {
  const [discountType, setDiscountType] = useState<discountType>()
  const [discountId, setDiscountId] = useState<string>()
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null>>()
  const [clientSecret, setClientSecret] = useState<string>()
  const [priceDetails, setPriceDetails] = useState<IPriceDetails>()
  const [order, setOrder] = useState<IOrder>()
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string>()
  const nights = props.startDate && props.endDate && props.endDate.hour(12).diff(props.startDate.hour(12), "days")
  const [userParams, userParamsDispatch] = useReducer(userParamsReducer, userParamsInitialState)

  const {
    campingStyle,
    selectedAddOns,
    userElectric,
    selectedInterval,
    userPets,
    couponCode,
    camp,
    selectedSite,
  } = useContext(BookerContext)

  useEffect(() => {
    setStripePromise(loadStripe(stripePublicKey, { stripeAccount: props.camp.stripe_account_id }))
  }, [props.camp.stripe_account_id])

  const fetchPriceDetails = useDebouncedCallback(() => {
    const hasRequiredProps = props.camp && campingStyle && selectedSite && props.startDate && props.endDate

    if (!hasRequiredProps) return

    api2.PriceDetails.orderForSites({ site_id: selectedSite.id }, {
      startDate: props.startDate?.toISOString(),
      endDate: props.endDate?.toISOString(),
      discountSlug: discountType,
      couponCodes: [couponCode],
      campingStyleId: campingStyle.id,
      number_of_adults: props.userPassengers,
      number_of_kids: props.userKidCampers,
      number_of_pets: userPets,
      user_electric: userElectric,
      add_ons: selectedAddOns,
      interval_id: selectedInterval?.id,

      // NOTE: we should have a separate endpoint for the booker and the following should be assumed
      // in the backend
      kind: 'occasional',
      subtotalOverride: null,
      isTaxEnabled: true,
      is_passenger_fees_enabled: true,
      isPetsFeeEnabled: true,
      isHolidayEnabled: true,
      isWeekendEnabled: true,
      isServiceFeeApplied: true,
      isCreditCardFeeEnabled: true,
      line_items_attributes: []
    })
      .then(({ order, price_detail: pd }) => {
        setPriceDetails(pd)
        setOrder(order)
        setDiscountRates(discountRatesFor(props.camp, campingStyle, pd?.nights))
        setIsLoading(false)
      })
      .catch((err) => {
        console.error(err)
        // Sentry.captureException(err)
        setIsLoading(false)
        setErrorMessage("Something went wrong. Please reload the page")
      })
  }, 150)

  const [discountValid, setDiscountValid] = useState(false)
  const [discountRates, setDiscountRates] = useState(discountRatesFor(props.camp, campingStyle, nights))

  useEffect(() => {
    if (
      props.camp.max_discounted_nights &&
      props.startDate &&
      props.endDate &&
      props.endDate.diff(props.startDate, "days") > props.camp.max_discounted_nights
    ) {
      setDiscountType(undefined)
      setDiscountId("")
    }
  }, [props.startDate, props.endDate])

  useEffect(() => {
    fetchPriceDetails()
  }, [selectedSite, props.startDate, props.endDate, selectedAddOns])

  const stripeOptions = useMemo(() => stripeOptionsFn(
    priceDetails?.total,
    camp,
    undefined,
    userParams.is_saving_payment_method ? 'off_session' : null
  ), [priceDetails?.total, camp, userParams.is_saving_payment_method])

  if (!stripePromise || !priceDetails || !priceDetails?.total) return <></>

  return (
    <Transition visible={props.visible} animation="fade right">
      <div>
        {!(priceDetails && priceDetails.total) ? (
          <div className="w-full flex justify-center items-center">
            <Spinner size={5} />
          </div>
        ) : (
          <Elements stripe={stripePromise} options={stripeOptions}>
            <PriceDetails
              camp={props.camp}
              selectedSite={props.site}
              nights={nights}
              reservationStartDate={props.startDate}
              reservationEndDate={props.endDate}
              discountType={discountType}
              setDiscountType={setDiscountType}
              discountId={discountId}
              setDiscountId={setDiscountId}
              userPassengers={props.userPassengers}
              userKidCampers={props.userKidCampers}
              stripePromise={stripePromise}
              site={props.site}
              setIsPaying={props.setIsPaying}
              userRigType={props.userRigType}
              userRigLength={props.userRigLength}
              userElectric={props.userElectric}
              onError={props.onError}
              order={order as IOrder}
              priceDetails={priceDetails}
              setPriceDetails={setPriceDetails}
              discountValid={discountValid}
              setDiscountValid={setDiscountValid}
              discountRates={discountRates}
              fetchPriceDetails={fetchPriceDetails}
              isLoading={isLoading}
              setIsLoading={setIsLoading}
              errorMessage={errorMessage}
              setErrorMessage={setErrorMessage}
              userParams={userParams}
              userParamsDispatch={userParamsDispatch}
            />
          </Elements>
        )}
      </div>
    </Transition>
  )
}

export default Payment
